/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 */

import { z } from 'zod';
import { hasUnsavedData } from './unsaved.js';

// -----------------------------------------------------------------------------
// Boundary schemas (issue #162)
// -----------------------------------------------------------------------------
// Backend response shapes for the three auth endpoints are codified as Zod
// schemas. `.parse()` is invoked at the boundary (after `await res.json()`) so
// backend contract drift throws an `AuthResponseError` immediately rather than
// surfacing as a downstream type error somewhere far from the cause.
//
// Conventions follow `src/services/keys/lookup.ts` and
// `src/services/vault/schema.ts`: schemas live in this file, types are exported
// via `z.infer`, and a custom error class with a `reason` code mirrors
// `BundleSignatureError`.

/**
 * Non-empty refresh-token string. Issue #113 enforces this at the boundary so
 * a misbehaving backend returning an object/array/boolean/empty string can no
 * longer silently corrupt localStorage (the legacy `"[object Object]"` bug).
 */
const NonEmptyRefreshToken = z
  .string()
  .min(1, { error: 'refreshToken must be a non-empty string' });

/**
 * Non-empty string that splits into exactly 3 dot-separated segments —
 * the canonical JWT shape (header.payload.signature). The refine is the
 * boundary-parser half of the #183 doubled-JWT guard: a backend
 * response shipping a concatenated `a.b.c a.b.c` token gets rejected
 * at parse time with `AuthResponseError` before any write to
 * localStorage. The matching write-boundary guard lives in
 * `setAccessToken`'s `_assertWellFormedJwt`.
 */
const JwtTokenString = z
  .string()
  .min(1)
  .refine(t => t.split('.').length === 3, {
    error: issue =>
      `JWT must have exactly 3 dot-separated segments, got ${(issue.input as string).split('.').length}`,
  });

/**
 * Claims persisted to localStorage. `.passthrough()` lets standard JWT claims
 * (`iat`, `iss`, `nbf`, `exp`, `sub`) plus future custom claims flow through;
 * the frontend only consumes `email` and `groups`.
 *
 * Note: `setUser` is called with two different shapes today — a decoded JWT
 * payload (from `setAccessToken`) and a `data.claims` object (from `login`).
 * Both happen to share `email`/`groups`, which is all this schema requires.
 */
export const UserClaimsSchema = z
  .object({
    email: z.string().min(1),
    groups: z.array(z.string()).optional(),
  })
  .passthrough();
export type UserClaims = z.infer<typeof UserClaimsSchema>;

export const LoginResponseSchema = z.object({
  token: JwtTokenString,
  // Optional: backend legitimately omits when it's not rotating. When present,
  // it must be a non-empty string (#113).
  refreshToken: NonEmptyRefreshToken.optional(),
  claims: UserClaimsSchema,
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/**
 * `POST /api/auth/refresh` response (#238 contract alignment).
 *
 * The endpoint is being implemented in app-backend#138, whose stated
 * plan is "returning the same `LoginResponse` shape" — i.e.
 * `{token, refreshToken, claims}` — while this frontend historically
 * expected `{accessToken, refreshToken?}`. Until the backend ships and
 * the contract is pinned for real, accept **both** spellings and
 * normalise to `{accessToken, refreshToken?}`; each variant keeps the
 * #183 3-segment JWT guard and the #113 non-empty refresh-token rule.
 * The exact expected contract is documented on app-backend#138.
 */
export const RefreshResponseSchema = z.union([
  z.object({
    accessToken: JwtTokenString,
    // Same optional-but-non-empty contract on refresh.
    refreshToken: NonEmptyRefreshToken.optional(),
  }),
  z
    .object({
      token: JwtTokenString,
      refreshToken: NonEmptyRefreshToken.optional(),
      claims: UserClaimsSchema.optional(),
    })
    .transform(({ token, refreshToken }) => ({
      accessToken: token,
      ...(refreshToken !== undefined ? { refreshToken } : {}),
    })),
]);
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

export const ChallengeResponseSchema = z
  .object({
    identifier: z.string().min(1),
    url: z.string().min(1),
  })
  .passthrough();
export type ChallengeResponse = z.infer<typeof ChallengeResponseSchema>;

/**
 * Shape of `POST /api/auth/register`'s response. Issue #183: the only
 * field the frontend currently consumes is `token`, which gets
 * persisted via `setAccessToken`. Before this PR the response was
 * consumed via a bare `as RegisterResponse` cast in `TdOnboarding` —
 * the boundary parser closes that gap so a doubled-token response is
 * caught at parse time (alongside the write-boundary guard in
 * `setAccessToken`).
 */
export const RegisterResponseSchema = z
  .object({
    token: JwtTokenString,
  })
  .passthrough();
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

/** Sent body for `POST /api/auth/login/<url>`. */
export interface LoginPayload {
  email: string;
  key: string;
}

/** What `formChallenge` returns to the caller (a UI-affordance result). */
export type ChallengeFormResult =
  (ChallengeResponse & { ok: true }) | { ok: false; error: Error };

/**
 * Thrown by the boundary parsers when a backend response fails its Zod schema.
 * The `reason` discriminator lets callers distinguish login/refresh/challenge
 * failures without parsing the message string.
 */
export class AuthResponseError extends Error {
  readonly reason:
    | 'malformed-login-response'
    | 'malformed-refresh-response'
    | 'malformed-challenge-response'
    | 'malformed-register-response';
  readonly issues: z.core.$ZodIssue[];

  constructor(reason: AuthResponseError['reason'], zodError: z.ZodError) {
    super(`${reason}: ${zodError.message}`);
    this.name = 'AuthResponseError';
    this.reason = reason;
    this.issues = zodError.issues;
  }
}

/** Thrown by `decodeToken` when the JWT cannot be split + base64-decoded. */
export class InvalidTokenError extends Error {
  constructor(message: string = 'Invalid token format') {
    super(message);
    this.name = 'InvalidTokenError';
  }
}

/**
 * Thrown by `setAccessToken`'s write-boundary guard (#183) when the
 * supplied string doesn't match the canonical 3-segment JWT shape.
 * The `reason` discriminator lets callers + tests distinguish the
 * two failure modes:
 *
 *   - `'doubled-jwt'`: more than 3 segments (the #183 reproducer —
 *     a concatenated `a.b.c a.b.c` lands in localStorage as one entry).
 *   - `'missing-segments'`: fewer than 3 segments (corruption, an
 *     unencoded placeholder string, or a backend ID confusion).
 *
 * Thrown *before* any localStorage write, so the corrupt value is
 * never persisted. Callers (`login`, `refreshAccessToken`,
 * `TdOnboarding.register`) propagate via their existing catch blocks.
 */
export class MalformedTokenError extends Error {
  readonly reason: 'doubled-jwt' | 'missing-segments';
  readonly segmentCount: number;

  constructor(segmentCount: number) {
    const reason: MalformedTokenError['reason'] =
      segmentCount > 3 ? 'doubled-jwt' : 'missing-segments';
    super(
      `MalformedTokenError (${reason}): JWT must have exactly 3 dot-separated segments, got ${segmentCount}`
    );
    this.name = 'MalformedTokenError';
    this.reason = reason;
    this.segmentCount = segmentCount;
  }
}

/**
 * Thrown by `refreshAccessToken` when the backend answers the refresh
 * call with `404 Not Found` — i.e. the deployment predates the
 * `/api/auth/refresh` route (app-backend#138). Distinct from the
 * generic refresh failure (#181: transient errors must preserve the
 * session) because a missing route is *deterministic*: retrying can
 * never succeed, so the session is cleanly ended instead of looping
 * 401 → refresh → 404 forever (#238).
 */
export class RefreshEndpointMissingError extends Error {
  constructor() {
    super(
      'Refresh endpoint not available (backend predates /api/auth/refresh)'
    );
    this.name = 'RefreshEndpointMissingError';
  }
}

const parseLoginResponse = (raw: unknown): LoginResponse => {
  const result = LoginResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-login-response', result.error);
  }
  return result.data;
};

/**
 * Boundary parser for `POST /api/auth/register`'s response (#183).
 * Throws `AuthResponseError('malformed-register-response', ...)` on
 * shape mismatch — in particular, a doubled JWT in `token` fails the
 * `JwtTokenString` 3-segment refine and lands here.
 */
export const parseRegisterResponse = (raw: unknown): RegisterResponse => {
  const result = RegisterResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-register-response', result.error);
  }
  return result.data;
};

const parseRefreshResponse = (raw: unknown): RefreshResponse => {
  const result = RefreshResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-refresh-response', result.error);
  }
  return result.data;
};

const parseChallengeResponse = (raw: unknown): ChallengeResponse => {
  const result = ChallengeResponseSchema.safeParse(raw);
  if (!result.success) {
    throw new AuthResponseError('malformed-challenge-response', result.error);
  }
  return result.data;
};

// -----------------------------------------------------------------------------
// AuthService — JWT auth surface
// -----------------------------------------------------------------------------

/**
 * AuthService handles JWT-backed authentication.
 *
 * Storage model (#242, see `docs/auth-token-storage.md`):
 *   - access token + user claims: **in-memory only** — never written to
 *     Web Storage, so an XSS payload cannot exfiltrate a replayable
 *     credential from storage. Cost: a page reload requires re-auth
 *     (mitigated by silent refresh once app-backend#138 lands).
 *   - refresh token: localStorage *interim only* — today's backend
 *     value is a non-secret placeholder; the target is an httpOnly
 *     cookie (recommendation recorded on app-backend#138).
 *   - refresh hint (account email): localStorage — PII but not a
 *     credential; enables boot-time silent refresh after a reload.
 */
export class AuthService {
  /** Retired key (#242) — cleaned up in the constructor, never written. */
  private readonly legacyAccessTokenKey = 'triggered_disclosure_access_token';
  private readonly refreshTokenKey = 'triggered_disclosure_refresh_token';
  /** Retired key (#242) — cleaned up in the constructor, never written. */
  private readonly legacyUserKey = 'triggered_disclosure_user';
  private readonly refreshHintKey = 'triggered_disclosure_refresh_hint';

  /** #242: in-memory access token — lost on reload by design. */
  private accessToken: string | null = null;
  /** #242: in-memory user claims — derived from the JWT / login response. */
  private user: UserClaims | null = null;

  constructor() {
    // #242: one-time cleanup of secrets persisted by pre-#242 builds so
    // they don't linger on returning devices. Best-effort — storage may
    // be unavailable (private mode), in which case nothing was
    // persisted anyway.
    try {
      localStorage.removeItem(this.legacyAccessTokenKey);
      localStorage.removeItem(this.legacyUserKey);
    } catch {
      /* storage unavailable */
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  setAccessToken(token: string): void {
    // #183: write-boundary shape guard. Rejects doubled JWTs (a
    // concatenated `a.b.c a.b.c` lands as a single 7-segment string
    // when split on `.`) and other malformed inputs before the value
    // is stored. Throws `MalformedTokenError` with a `reason`
    // discriminator so callers + tests can tell the two failure modes
    // apart without grepping the message. #242 moved the slot from
    // localStorage to memory; the guard fences the same boundary.
    this._assertWellFormedJwt(token);
    const payload = this.decodeToken(token);
    this.setUser(payload);
    this.accessToken = token;
  }

  private _assertWellFormedJwt(token: string): void {
    const segmentCount = token.split('.').length;
    if (segmentCount !== 3) {
      throw new MalformedTokenError(segmentCount);
    }
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  /**
   * Persist `refreshToken` if (and only if) the backend supplied a well-formed
   * non-empty string. Centralises the validation so both `login` and
   * `refreshAccessToken` agree on the shape contract.
   *
   * Shape validation now happens in the boundary parsers
   * (`parseLoginResponse` / `parseRefreshResponse`) — this method assumes a
   * pre-parsed `string | undefined` and only handles the persistence branch.
   * Absent (`undefined`) is **not** an error — the refresh endpoint
   * legitimately omits the field when the backend isn't rotating. The caller's
   * pre-existing refresh token stays in place.
   */
  private _persistRefreshTokenFrom(refreshToken: string | undefined): void {
    if (refreshToken !== undefined) {
      this.setRefreshToken(refreshToken);
    }
  }

  removeTokens(): void {
    this.accessToken = null;
    localStorage.removeItem(this.refreshTokenKey);
  }

  /**
   * Read the in-memory user claims (#242). `null` after a reload or
   * logout — the pre-#242 localStorage corruption handling (#162) is
   * obsolete because nothing is parsed from storage anymore.
   */
  getUser(): UserClaims | null {
    return this.user;
  }

  /**
   * Account email persisted for boot-time silent refresh (#242). PII
   * but not a credential — the refresh endpoint takes
   * `{email, refreshToken}` and the in-memory claims are gone after a
   * reload. Cleared by `removeUser` (logout).
   */
  getRefreshHintEmail(): string | null {
    return localStorage.getItem(this.refreshHintKey);
  }

  getEmail(): string | undefined {
    return this.getUser()?.email;
  }

  isMember(): boolean {
    return this.getUser()?.groups?.includes('member') ?? false;
  }

  isAdmin(): boolean {
    return this.getUser()?.groups?.includes('admin') ?? false;
  }

  setUser(user: UserClaims): void {
    this.user = user;
    // #242: persist only the email as the silent-refresh hint —
    // claims themselves stay in memory.
    try {
      localStorage.setItem(this.refreshHintKey, user.email);
    } catch {
      /* storage unavailable — hint is an optimisation, not a dependency */
    }
  }

  removeUser(): void {
    this.user = null;
    localStorage.removeItem(this.refreshHintKey);
  }

  /**
   * Resolve whether the user has a usable access token.
   *
   * #181: this used to be synchronous and fire-and-forget the refresh on
   * expiry — so the sole caller (`initApp` in `main.js`) routed to the login
   * page while a successful refresh was still in flight. It also called
   * `logout()` (which navigates) on malformed-token decode, turning a
   * corrupt-storage edge case into a hard redirect. The contract is now async;
   * callers must `await`.
   *
   *  - no token → `false` (no side effects).
   *  - decode failure → clears the garbage from storage, returns `false`.
   *    **Does not navigate.** Caller routes to login on its own.
   *  - expired token → awaits a real refresh. Success → `true` with the new
   *    token already persisted. Failure → `false`, **tokens preserved** so a
   *    transient network blip can't masquerade as a server-side session
   *    termination (the original bug).
   *  - valid unexpired token → `true`.
   */
  async isAuthenticated(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) return false;

    let payload: UserClaims & { exp?: unknown };
    try {
      payload = this.decodeToken(token);
    } catch (error) {
      console.error('Token validation error:', error);
      this.removeTokens();
      this.removeUser();
      return false;
    }

    const currentTime = Date.now() / 1000;
    // RFC 7519 §4.1.4: `exp` is "the time on or after which the JWT MUST NOT
    // be accepted" — so `<=` (not `<`) is the spec-correct comparison. The
    // explicit numeric type guard replaces the prior truthy guard, which
    // would silently treat `exp: 0` (epoch) as "no expiry set" and a
    // non-numeric `exp` (e.g. a string from a misbehaving backend) as
    // never-expiring via `NaN` comparison.
    if (typeof payload.exp === 'number' && payload.exp <= currentTime) {
      try {
        await this.refreshAccessToken();
        return true;
      } catch (error) {
        console.error('Token refresh failed during isAuthenticated:', error);
        return false;
      }
    }
    return true;
  }

  /**
   * Decode the payload section of a JWT. Returns the parsed claims (typed as
   * `UserClaims` via `.passthrough()` so any extra JWT fields like `exp` flow
   * through). Throws `InvalidTokenError` on any structural failure.
   */
  decodeToken(token: string): UserClaims {
    try {
      const segments = token.split('.');
      const base64Url = segments[1];
      if (!base64Url) throw new Error('missing payload segment');
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return UserClaimsSchema.parse(JSON.parse(jsonPayload));
    } catch {
      throw new InvalidTokenError();
    }
  }

  async login(payload: LoginPayload, url: string): Promise<LoginResponse> {
    try {
      const response = await fetch(`/api/auth/login/${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Login failed ${response.status}: ${response.statusText}`
        );
      }

      const data = parseLoginResponse(await response.json());

      // Store tokens and user data. F17 (#57): persist the real refresh token
      // returned by the backend. #113: shape-validation happens inside
      // `parseLoginResponse` above — a malformed `refreshToken` (object,
      // array, boolean, empty string) throws `AuthResponseError` and we never
      // reach the writes.
      this.setAccessToken(data.token);
      this._persistRefreshTokenFrom(data.refreshToken);
      this.setUser(data.claims);

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async requestChallenge(email: string): Promise<ChallengeResponse> {
    const response = await fetch('/api/auth/requestchallenge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new Error(
        `Challenge request failed ${response.status}: ${response.statusText}`
      );
    }
    return parseChallengeResponse(await response.json());
  }

  /**
   * Exchange the stored refresh token for a fresh access token via
   * `POST /api/auth/refresh` (contract documented on app-backend#138;
   * both `{accessToken}` and `LoginResponse`-style `{token}` response
   * spellings are accepted, see `RefreshResponseSchema`).
   *
   * Failure semantics:
   *  - **404** → `RefreshEndpointMissingError`, session cleanly ended
   *    (#238: the backend predates the route; retrying cannot succeed).
   *  - any other failure → generic throw with the session **preserved**
   *    (#181: transient errors must not wipe a valid session).
   *
   * @returns the new access token (already stored in memory)
   */
  async refreshAccessToken(): Promise<string> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // #242: after a reload the in-memory claims are gone — fall back
      // to the persisted refresh hint so boot-time silent refresh works
      // once app-backend#138 ships a real refresh endpoint.
      const currentEmail = this.getUser()?.email ?? this.getRefreshHintEmail();
      if (!currentEmail) {
        throw new Error('No stored user email available for refresh');
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ email: currentEmail, refreshToken }),
      });

      // #238: 404 means the backend deployment predates the refresh
      // route entirely (app-backend#138) — deterministic, not
      // transient. End the session cleanly (memory + refresh token +
      // hint; NO navigation, NO confirm) so the next `tdFetch` fails
      // fast on 'No token available' instead of looping
      // 401 → refresh → 404. This deliberately does not reuse
      // `logout()` — the #247 unsaved-data confirm and the redirect
      // don't belong in a background refresh path.
      if (response.status === 404) {
        this.removeTokens();
        this.removeUser();
        throw new RefreshEndpointMissingError();
      }

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      // Shape-validate the response *before* any writes. Until #181 this
      // didn't matter because the catch-block called `logout()` and wiped
      // both tokens; without that wipe, a write-then-throw ordering would
      // leave a rotated access token paired with the prior refresh token —
      // an inconsistent partial update visible to subsequent calls. The
      // boundary parse throws `AuthResponseError` on shape mismatch (#113)
      // and we commit both writes atomically once the shape is known good.
      const data = parseRefreshResponse(await response.json());
      this.setAccessToken(data.accessToken);
      this._persistRefreshTokenFrom(data.refreshToken);

      return data.accessToken;
    } catch (error) {
      // #181: do NOT call `logout()` here. A transient network blip is
      // indistinguishable from a definitive token rejection at this layer,
      // and the side-effect (`removeTokens` + `window.location.href = '/'`)
      // was wiping valid sessions on the slightest hiccup. Callers
      // (`isAuthenticated`, `tdfetch.sharedRefresh`) already handle a
      // rejected promise gracefully without needing the token wipe.
      // (#238 exception: the 404 branch above already ended the session
      // deliberately — a missing route is deterministic, not transient.)
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Clear tokens + user and navigate to the login page.
   *
   * #247: when any registered service (today: the vault) reports
   * unsaved data, the user is asked to confirm first — logging out
   * discards the in-memory vault state, so silently proceeding would
   * lose e.g. TOFU pins or document keys not yet saved to the server.
   * Declining the confirm aborts the logout entirely (no token wipe,
   * no navigation).
   */
  logout(): void {
    if (
      hasUnsavedData() &&
      !window.confirm(
        'You have unsaved changes that will be lost. Log out anyway?'
      )
    ) {
      return;
    }
    this.removeTokens();
    this.removeUser();
    window.location.href = '/';
  }

  getUserFromToken(): UserClaims | null {
    const token = this.getAccessToken();
    if (!token) return null;
    try {
      return this.decodeToken(token);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Backward-compat aliases. Kept until callers migrate (out of scope here).
  getToken(): string | null {
    return this.getAccessToken();
  }
  setToken(token: string): void {
    this.setAccessToken(token);
  }
  removeToken(): void {
    this.removeTokens();
  }

  async challengeLogin(
    code: string,
    challengeResult: ChallengeResponse,
    errorMessage: HTMLElement,
    submitBtn: HTMLButtonElement
  ): Promise<boolean> {
    try {
      submitBtn.textContent = 'Signing in...';
      submitBtn.disabled = true;
      errorMessage.hidden = true;

      const loginData: LoginPayload = {
        email: challengeResult.identifier,
        key: code,
      };
      await this.login(loginData, challengeResult.url);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Login error:', error, message);
      errorMessage.textContent = `Login failed. ${message}`;
      errorMessage.hidden = false;

      submitBtn.textContent = 'Sign In';
      submitBtn.disabled = false;
      return false;
    }
    return true;
  }

  async formChallenge(
    form: HTMLElement,
    errorMessage: HTMLElement
  ): Promise<ChallengeFormResult> {
    const submitBtn = form.querySelector(
      'wa-button'
    ) as HTMLButtonElement | null;
    try {
      if (submitBtn) {
        submitBtn.textContent = 'Requesting code...';
        submitBtn.disabled = true;
      }
      errorMessage.hidden = true;

      const emailInput = form.querySelector('wa-input[name="email"]') as
        (HTMLElement & { value?: string }) | null;
      const email = emailInput?.value ?? '';

      const result = await this.requestChallenge(email);
      return { ...result, ok: true };
    } catch (error) {
      console.error('Challenge error:', error);
      errorMessage.textContent = 'Challenge failed. Please try again.';
      errorMessage.hidden = false;

      if (submitBtn) {
        submitBtn.textContent = 'Request Code';
        submitBtn.disabled = false;
      }
      return {
        ok: false,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
